/**
 * web/src/lib/promotionCard.ts
 * Shared helpers for the speaker promotion gate's board-card attachment, used
 * by both the single-gate page (GateReview) and the batch review queue
 * (ReviewQueue) so they surface the card identically.
 *
 * The promotion email attaches the speaker card the backend re-reads from the
 * monday "Speaker Card" column at send time. The gate's `gateData.mondayCard`
 * is a status marker only (`{ status, fileName? }` — no image bytes/URL); the
 * actual image is served through the authenticated proxy at
 * `/api/workflows/:id/card-candidates/board_card`, which reads the SAME monday
 * column, so a thumbnail from it matches exactly what will attach.
 */
import type { PinnedAttachment } from '../components/gates/EmailPreview';

/**
 * The promotion gate's `gateData.mondayCard` (board-card status the gate build
 * stamps), defensively parsed. Null when absent or malformed.
 *
 * @param gateData the promotion step's raw gate data
 * @returns `{ status?, fileName? }` or null
 * @sideEffects none
 * @idempotent yes
 */
export function readMondayCard(
  gateData: unknown,
): { status?: string; fileName?: string } | null {
  if (!gateData || typeof gateData !== 'object') return null;
  const mc = (gateData as Record<string, unknown>).mondayCard;
  if (!mc || typeof mc !== 'object') return null;
  const status = (mc as { status?: unknown }).status;
  const fileName = (mc as { fileName?: unknown }).fileName;
  return {
    status: typeof status === 'string' ? status : undefined,
    fileName: typeof fileName === 'string' ? fileName : undefined,
  };
}

/**
 * The promotion email's pinned-attachment chip: the speaker card the backend
 * re-reads from the monday board and attaches at approve time, WITH a preview
 * URL pointing at the same board column so the reviewer sees a thumbnail of the
 * exact card. Only returned when the gate build found a card (`status === 'ok'`)
 * and we have a workflow id to build the proxy URL.
 *
 * @param workflowId the promotion gate's workflow id (for the proxy URL)
 * @param gateData the promotion step's raw gate data
 * @returns one-element pinned list, or undefined to pin nothing
 * @sideEffects none
 * @idempotent yes
 */
export function promotionCardPinned(
  workflowId: string | undefined,
  gateData: unknown,
): PinnedAttachment[] | undefined {
  const card = readMondayCard(gateData);
  if (card?.status !== 'ok' || !workflowId) return undefined;
  return [
    {
      filename: card.fileName || 'Speaker card (monday board)',
      note: 'from the monday board · added on send',
      previewUrl: `/api/workflows/${workflowId}/card-candidates/board_card`,
    },
  ];
}
